import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { callStartTrial } from '@/lib/api';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { Sparkles, Target, Zap } from 'lucide-react';

// Trial entry point. If user isn't signed in (or is anon), we sign them in
// anonymously, then call startTrial, then route to /trial/:sessionId with the
// slate handed off via location state.
//
// Visually: a brief "we're warming up your trial" card with what-to-expect
// chips so the user knows they have ~10 min of work ahead and immediate
// feedback per question. Auto-routes when ready.

export function TrialStart() {
  const { user, signInAnonymous, loading } = useAuth();
  const nav = useNavigate();
  const startedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        if (!user) {
          await signInAnonymous();
          startedRef.current = false;
          return;
        }
        const resp = await callStartTrial({});
        nav(`/trial/${resp.sessionId}`, {
          replace: true,
          state: { questions: resp.questions, mode: 'trial', warnings: resp.warnings },
        });
      } catch (e) {
        const msg = (e as { message?: string }).message ?? 'Could not start the trial.';
        setError(msg);
        startedRef.current = false;
      }
    })();
  }, [user, loading, signInAnonymous, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-brand-50/40 via-stone-50 to-emerald-50/30 dark:from-brand-900/20 dark:via-stone-950 dark:to-emerald-900/15">
      <div className="w-full max-w-md">
        {error ? (
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-rose-200 dark:border-rose-800 p-6 text-center shadow-sm">
            <h1 className="text-lg font-semibold mb-2">We couldn't start your trial</h1>
            <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">{error}</p>
            <Button onClick={() => location.reload()}>Try again</Button>
          </div>
        ) : (
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6 sm:p-7 shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-brand-100 dark:bg-brand-900/40 animate-ping opacity-50" />
                <div className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-100 dark:bg-brand-900/50">
                  <Sparkles className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                </div>
              </div>
            </div>
            <h1 className="text-center text-xl sm:text-2xl font-bold tracking-tight">
              Your free CNA trial is starting
            </h1>
            <p className="text-center text-sm text-stone-600 dark:text-stone-400 mt-1.5">
              No signup needed — get a real readiness score in about 10 minutes.
            </p>

            {/* What to expect chips */}
            <div className="grid grid-cols-3 gap-2 mt-5">
              <Chip icon={<Target className="h-4 w-4" />} label="25 Qs" />
              <Chip icon={<Zap className="h-4 w-4" />} label="Instant feedback" />
              <Chip icon={<Sparkles className="h-4 w-4" />} label="Readiness score" />
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-stone-500">
              <Spinner className="h-4 w-4 text-brand-600" />
              <span>{user ? 'Picking your questions…' : 'Setting up…'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/40 dark:bg-stone-900/40 py-2.5">
      <span className="text-brand-600 dark:text-brand-400">{icon}</span>
      <span className="text-[12px] font-medium text-stone-700 dark:text-stone-300 text-center">{label}</span>
    </div>
  );
}
